import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Globe, RefreshCw, TriangleAlert } from 'lucide-react';
import { Grid, Row, Section } from './form';
import { Alert, Badge, Button, Input } from './ui';
import { api } from '../lib/api';

interface DnsState {
  resolves: boolean;
  resolvedTo: string[];
  matches: boolean;
  looksProxied: boolean;
}

interface SetupStatus {
  hostname: string | null;
  publicIp: string | null;
  dns: DnsState | null;
  hostnameLocked: boolean;
  certificateReady: boolean;
}

interface ChangePreview {
  current: string | null;
  next: string;
  dns: DnsState;
  publicIp: string | null;
  extensionsAffected: number;
  unchanged: boolean;
}

/**
 * The hostname this PBX answers to, and whether DNS agrees.
 *
 * This tab used to render the first-run wizard, which meant it showed a "Ready" card with a "Go
 * to the dashboard" button and no way to change anything — a status page that could not report
 * the current state and a settings page that could not settle anything.
 *
 * The hostname is load-bearing in a way that is not obvious: it is the SIP realm, so it is baked
 * into every credential, and it is what Caddy will request a certificate for. So this screen shows
 * what is true now, and makes a change possible while being explicit about the cost.
 */
export function HostnameAndDns() {
  const queryClient = useQueryClient();
  const [next, setNext] = useState('');
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/api/v1/setup/status'),
  });

  const recheck = useMutation({
    mutationFn: () => api.post('/api/v1/setup/verify-dns', {}),
    onSuccess: () => {
      setNote('DNS re-checked.');
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
    },
  });

  const check = useMutation({
    mutationFn: (hostname: string) =>
      api.post<ChangePreview>('/api/v1/setup/hostname/preview', { hostname }),
    onSuccess: setPreview,
  });

  const change = useMutation({
    mutationFn: (hostname: string) =>
      api.post<{ hostname: string; credentialsRebuilt: number }>('/api/v1/setup/hostname', {
        hostname,
        confirm: 'change-hostname',
      }),
    onSuccess: (result) => {
      setPreview(null);
      setNext('');
      setNote(
        `Now answering to ${result.hostname}. ` +
          `${result.credentialsRebuilt} extension credential(s) rebuilt. ` +
          `Move to https://${result.hostname} — this address stops being renewed.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
    },
  });

  if (isLoading || !status) return <p className="text-sm text-muted-foreground">Checking…</p>;

  const dns = status.dns;
  const healthy = dns?.matches === true && !dns.looksProxied;

  return (
    <div className="space-y-5">
      {note && <Alert>{note}</Alert>}

      <Section title="This PBX answers to">
        {status.hostname ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Globe className="size-4 text-muted-foreground" />
              <a
                href={`https://${status.hostname}`}
                className="font-mono text-lg font-medium hover:underline"
              >
                {status.hostname}
              </a>
              <Badge tone={healthy ? 'success' : 'warning'}>
                {healthy ? 'DNS and certificate healthy' : 'Needs attention'}
              </Badge>
            </div>

            <Grid>
              <Row label="This server's public address">
                <div className="flex h-9 items-center font-mono text-sm">
                  {status.publicIp ?? 'could not be determined'}
                </div>
              </Row>
              <Row
                label="What the hostname resolves to"
                hint="Asked of public resolvers, not this server's cache — that is the view Let's Encrypt validates against."
              >
                <div className="flex h-9 items-center gap-2 font-mono text-sm">
                  {dns?.resolves ? dns.resolvedTo.join(', ') : 'does not resolve'}
                  {dns?.matches ? (
                    <Check className="size-4 text-emerald-500" />
                  ) : (
                    <TriangleAlert className="size-4 text-amber-500" />
                  )}
                </div>
              </Row>
            </Grid>

            {/*
              Named rather than reported as a generic mismatch. A proxied record is the single most
              common reason a correctly-created A record still does not work, and "DNS mismatch"
              sends people to re-check a record that is already right.
            */}
            {dns?.looksProxied && (
              <Alert>
                <strong>This record looks proxied</strong> (Cloudflare's orange cloud, or similar).
                The proxy terminates TLS, cannot carry UDP, and drops the SIP WebSocket subprotocol
                — so calls will not work. Set the record to DNS-only.
              </Alert>
            )}

            {dns?.resolves && !dns.matches && !dns.looksProxied && (
              <Alert>
                <strong>DNS points somewhere else.</strong> {status.hostname} resolves to{' '}
                {dns.resolvedTo.join(', ')}, but this server is {status.publicIp ?? 'unknown'}. No
                certificate will be issued until they match.
              </Alert>
            )}

            {!dns?.resolves && (
              <Alert>
                <strong>{status.hostname} does not resolve.</strong> Create an A record pointing it
                at {status.publicIp ?? 'this server'}, then re-check.
              </Alert>
            )}

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => recheck.mutate()}
                disabled={recheck.isPending}
              >
                <RefreshCw className={`mr-1.5 size-4 ${recheck.isPending ? 'animate-spin' : ''}`} />
                Re-check DNS
              </Button>
              <span className="text-sm text-muted-foreground">
                Certificate:{' '}
                {status.certificateReady
                  ? 'issued automatically on first visit over HTTPS'
                  : 'waiting for DNS to match'}
              </span>
            </div>
          </div>
        ) : (
          <Alert>No hostname is set yet. Finish the setup wizard first.</Alert>
        )}
      </Section>

      <Section
        title="Change the hostname"
        description="Moving to a different domain. Check it first — the preview says exactly what will happen."
      >
        <Grid>
          <Row label="New hostname" hint="A fully-qualified name, e.g. pbx.example.com.">
            <Input
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
                setPreview(null);
              }}
              placeholder="pbx.example.com"
              className="font-mono"
            />
          </Row>
          <Row label="">
            <div className="flex h-9 items-center">
              <Button
                variant="secondary"
                onClick={() => check.mutate(next.trim().toLowerCase())}
                disabled={!next.trim() || check.isPending}
              >
                Check this hostname
              </Button>
            </div>
          </Row>
        </Grid>

        {check.error && <Alert>{(check.error as Error).message}</Alert>}
        {change.error && <Alert>{(change.error as Error).message}</Alert>}

        {preview && <ChangeSummary preview={preview} />}

        {preview && !preview.unchanged && (
          /*
            A plain action row, not FormFooter — this is not a <form>, and FormFooter renders a
            type="submit" button that would sit there looking primary and doing nothing.
          */
          <div className="flex items-center gap-2 border-t border-border pt-4">
            <Button
              disabled={change.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Move this PBX to ${preview.next}?\n\n` +
                      `${preview.extensionsAffected} extension credential(s) will be rebuilt.\n` +
                      (preview.dns.matches
                        ? ''
                        : '\nWARNING: DNS for that name does not point at this server, so it will not get a certificate and you may lose access to this admin panel.\n') +
                      `\nAfterwards, use https://${preview.next}`,
                  )
                ) {
                  change.mutate(preview.next);
                }
              }}
            >
              {change.isPending
                ? 'Changing…'
                : preview.dns.matches
                  ? 'Change hostname'
                  : 'Change anyway'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setPreview(null)}
              disabled={change.isPending}
            >
              Cancel
            </Button>
          </div>
        )}
      </Section>

      {/*
        What actually happens, written down. The realm consequence is the one that surprises
        people, and the reassuring half — that desk phones sort themselves out — is just as
        important as the warning, or an admin schedules a maintenance window they do not need.
      */}
      <Section title="What changing it does">
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Every SIP credential is rebuilt.</strong> The
            hostname is the SIP realm, and a credential is a digest of the username, the realm and
            the password. The passwords are kept encrypted, so the digests are rebuilt here — nobody
            has to reset anything.
          </li>
          <li>
            <strong className="text-foreground">Desk phones need no attention.</strong> A phone
            holds its password and answers whatever realm Asterisk challenges it with, so it
            re-registers on its own timer.
          </li>
          <li>
            <strong className="text-foreground">Browser softphones reconnect themselves.</strong>{' '}
            They hold a digest rather than a password, so every live session is told to
            re-initialise as part of the change.
          </li>
          <li>
            <strong className="text-foreground">The old address keeps working for a while.</strong>{' '}
            A certificate already issued stays valid until it expires, but nothing renews it. Move
            your bookmarks, and any phones provisioned with the old name.
          </li>
        </ul>
      </Section>
    </div>
  );
}

function ChangeSummary({ preview }: { preview: ChangePreview }) {
  if (preview.unchanged) {
    return <Alert>That is already this PBX's hostname. Nothing to change.</Alert>;
  }

  return (
    <div className="space-y-3 rounded-[--radius] border border-border p-4">
      <p className="text-sm">
        <span className="font-mono">{preview.current ?? 'not set'}</span>
        <span className="mx-2 text-muted-foreground">→</span>
        <span className="font-mono font-medium">{preview.next}</span>
      </p>

      <div className="space-y-1.5 text-sm">
        <p className="flex items-center gap-2">
          {preview.dns.matches ? (
            <Check className="size-4 shrink-0 text-emerald-500" />
          ) : (
            <TriangleAlert className="size-4 shrink-0 text-amber-500" />
          )}
          {preview.dns.resolves
            ? preview.dns.matches
              ? `Resolves to ${preview.dns.resolvedTo.join(', ')} — this server.`
              : `Resolves to ${preview.dns.resolvedTo.join(', ')}, but this server is ${preview.publicIp ?? 'unknown'}.`
            : 'Does not resolve. Create an A record first.'}
        </p>

        {preview.dns.looksProxied && (
          <p className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
            <TriangleAlert className="size-4 shrink-0" />
            That record looks proxied. Set it to DNS-only or calls will not work.
          </p>
        )}

        <p className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="size-4 shrink-0" />
          {preview.extensionsAffected} extension credential(s) will be rebuilt automatically.
        </p>
      </div>

      {!preview.dns.matches && (
        <Alert>
          <strong>You may lose access to this admin panel.</strong> Certificates are issued only for
          the configured hostname, so if {preview.next} does not point here, it will not get one.
          Fix the DNS record first unless you are deliberately moving the server too.
        </Alert>
      )}
    </div>
  );
}
